import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

/**
 * RC-WEB-4 §4 — THE single unread source for every authenticated surface.
 * Header badge, nav badge, dashboard summary and the Messages page all read
 * from here, which reads only `/api/inbox/*`.
 */
const InboxContext = createContext({
  unread: 0,
  conversations: [],
  loading: false,
  refresh: () => {},
});

const API = "/api";
const POLL_MS = 20000;

export const InboxProvider = ({ children }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setConversations([]);
      setUnread(0);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inbox/conversations`);
      setConversations(res.data?.conversations || []);
      setUnread(res.data?.unread_total || 0);
    } catch (_e) {
      /* keep last known state */
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
    if (timer.current) clearInterval(timer.current);
    if (user) timer.current = setInterval(refresh, POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [refresh, user]);

  const value = useMemo(
    () => ({ unread, conversations, loading, refresh }),
    [unread, conversations, loading, refresh]
  );

  return <InboxContext.Provider value={value}>{children}</InboxContext.Provider>;
};

export const useInbox = () => useContext(InboxContext);
export default InboxContext;
